/**
 * Navigation helpers — shared across hubs so every list item can deep-link
 * to its source record consistently.
 *
 * The pattern: hubs (Actions, Pipeline, Revenue, Clients) read a `?focus=<id>`
 * query param on mount and use it to scroll the matching row into view +
 * highlight it briefly.
 */
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Returns the route prefix the user is currently under — `/dashboard` for
 * the live app, `/preview` for the sandboxed iframe view, or `/clients`
 * (`/preview/clients`) for the Clients platform. Hub pages (Clients, Actions,
 * DataHealth, Meetings, ClientDetail) are reused across the owner dashboard
 * AND the Clients platform, so their deep-links must resolve to whichever
 * tree they are mounted under.
 */
export function useBasePath(): string {
  const { pathname } = useLocation();
  if (pathname === '/preview/clients' || pathname.startsWith('/preview/clients/')) return '/preview/clients';
  if (pathname === '/clients' || pathname.startsWith('/clients/')) return '/clients';
  return pathname.startsWith('/preview') ? '/preview' : '/dashboard';
}

/**
 * Joins basePath + target safely. If `target` already starts with
 * `/dashboard/` or `/preview/`, it's returned untouched (avoids the
 * double-prefix bug). Otherwise prepended.
 */
export function joinNav(basePath: string, target: string | undefined | null): string {
  if (!target) return basePath;
  if (target.startsWith('/dashboard/') || target.startsWith('/preview/')) return target;
  // The Sales platform lives beside the dashboard, not under it (a briefing item links to /sales/deals/<id>).
  if (target.startsWith('/sales/')) return salesPath(basePath, target.slice('/sales'.length));
  if (target.startsWith('/')) return `${basePath}${target}`;
  return `${basePath}/${target}`;
}

/**
 * Read `?focus=<id>` from the URL. Hub components call this to know which
 * row to highlight + scroll to.
 */
export function useFocusedId(): string | null {
  const { search } = useLocation();
  const [id, setId] = useState<string | null>(null);
  useEffect(() => {
    const params = new URLSearchParams(search);
    setId(params.get('focus'));
  }, [search]);
  return id;
}

/**
 * Build a deep-link to a specific item inside a hub.
 *
 *   focusHref(basePath, '/actions', 'sig-123')
 *     → '/dashboard/actions?focus=sig-123'
 */
export function focusHref(basePath: string, hubPath: string, id: string): string {
  const prefix = joinNav(basePath, hubPath);
  return `${prefix}?focus=${encodeURIComponent(id)}`;
}

/**
 * Build a filter deep-link.
 *
 *   filterHref(basePath, '/revenue', { bucket: '31_60' })
 *     → '/dashboard/revenue?bucket=31_60'
 */
export function filterHref(
  basePath: string,
  hubPath: string,
  params: Record<string, string>,
): string {
  const prefix = joinNav(basePath, hubPath);
  const qs = new URLSearchParams(params).toString();
  return qs ? `${prefix}?${qs}` : prefix;
}

/**
 * Custom hook for "I just navigated here with ?focus=X — scroll to and
 * briefly highlight the element with id=`focus-${X}`".
 */
export function useScrollToFocused(): string | null {
  const focusedId = useFocusedId();
  useEffect(() => {
    if (!focusedId) return;
    // The focused row usually arrives with a network fetch, so keep looking
    // for ~3s instead of checking once after the first paint.
    let tries = 0;
    const t = setInterval(() => {
      const el = document.getElementById(`focus-${focusedId}`);
      if (!el && ++tries < 20) return;
      clearInterval(t);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('animate-focus-pulse');
      setTimeout(() => el.classList.remove('animate-focus-pulse'), 2000);
    }, 150);
    return () => clearInterval(t);
  }, [focusedId]);
  return focusedId;
}

/**
 * A path in the Sales app (`/sales/...`) from any tree. Sales lives at /sales,
 * or /preview/sales in the sandbox; `${basePath}/pipeline` is only right under
 * /dashboard and /preview, and was a dead link under the Clients platform
 * (/clients/pipeline).
 *
 *   salesPath('/clients', '/deals/abc') → '/sales/deals/abc'
 */
export function salesPath(basePath: string, path: string): string {
  return `${basePath.startsWith('/preview') ? '/preview' : ''}/sales${path}`;
}